import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import type { UpstreamApiCrossSurveyBenchmarkData } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { dataErrorHandling } from 'src/components/dashboard/helper';

const fetchCrossSurveyBenchmarkData = async (
  endpoint: string,
  flatFilters: Record<string, string[]>,
  signal?: AbortSignal,
): Promise<UpstreamApiCrossSurveyBenchmarkData> =>
  HttpService.post(endpoint, flatFilters, undefined, undefined, { signal })
    .then((data) => Promise.resolve(data.data))
    .catch(dataErrorHandling);

const useCrossSurveyBenchmarkDataQuery = (
  enable: boolean,
  dashboardId?: number,
  filters: Record<string, string[]> = {},
) => {
  const { i18n } = useTranslation();

  const endpoint = useMemo(
    () =>
      dashboardId
        ? generatePath(apiPaths.DASHBOARD.CROSS_SURVEY_BENCHMARK, {
            id: dashboardId,
          })
        : '',
    [dashboardId],
  );

  const flatFiltersLength = Object.keys(filters).length;
  return useQuery<UpstreamApiCrossSurveyBenchmarkData, string>({
    queryKey: [endpoint, i18n.language, filters],
    queryFn: ({ signal }) =>
      fetchCrossSurveyBenchmarkData(endpoint, filters, signal),
    enabled: enable && !!dashboardId && flatFiltersLength > 0,
  });
};

export default useCrossSurveyBenchmarkDataQuery;
